// src/pages/MachineDetailsPage.js

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, TextField, Alert, Grid, Select, MenuItem,
  FormControl, InputLabel, CardMedia, List, ListItem, ListItemText, Divider, 
  TableContainer, Table, TableHead, TableBody, TableRow, TableCell, CircularProgress,
  Card, CardContent, Chip, Avatar, alpha, useTheme, Stack, LinearProgress,
  Tabs, Tab
} from '@mui/material';
import {
  ArrowBack,
  Save,
  PlayCircleOutline,
  Add,
  Build,
  History,
  Analytics,
  Note,
  LocalLaundryService,
  Speed,
  Scale,
  Warning,
  CheckCircle
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

import { CLIENTS } from '../data/clients';

// ✅ Імпорт сервісу зберігання
import { getData, saveData } from '../services/storageService';

// --- Константи ---
const PROGRAMS = [
  { code: 21, name: '21 - MANTELERÍA (CALIENTE)' }, { code: 22, name: '22 - MANTELERÍA (FRÍO)' },
  { code: 23, name: '23 - SÁBANA (CALIENTE)' }, { code: 24, name: '24 - SÁBANA (FRÍO)' },
  { code: 25, name: '25 - COLOR (CALIENTE)' }, { code: 26, name: '26 - COLOR (FRÍO)' },
  { code: 27, name: '27 - RECHAZO (FRÍO)' }, { code: 30, name: '28 - RECHAZO (CALIENTE)' },
  { code: 29, name: '29 - Centrífugo' }, { code: 30, name: '30 - DESAPRESTO (CALIENTE)' }
];

const initialMachineData = [
  { id: 1, name: 'Máquina #1', capacity: 40, status: 'Disponible', model: 'GIRBAU HS 4040', imageUrl: `${process.env.PUBLIC_URL}/images/girbau_hs4040.jpg` },
  { id: 2, name: 'Máquina #2', capacity: 40, status: 'Requiere Servicio', model: 'Carbonell', imageUrl: `${process.env.PUBLIC_URL}/images/machine_broken.png` },
  { id: 3, name: 'Máquina #3', capacity: 60, maxLoad: 55, status: 'Disponible', model: 'GIRBAU HS 6057', imageUrl: `${process.env.PUBLIC_URL}/images/girbau_hs6057.jpg` },
  { id: 4, name: 'Máquina #4', capacity: 60, maxLoad: 55, status: 'En Uso', model: 'GIRBAU HS 6057', imageUrl: `${process.env.PUBLIC_URL}/images/girbau_hs6057.jpg` },
  { id: 5, name: 'Máquina #5', capacity: 20, status: 'Disponible', model: 'GIRBAU HS 6024', imageUrl: `${process.env.PUBLIC_URL}/images/girbau_hs6024.jpg` },
];

// 🎨 Компонент вкладок
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`machine-tabpanel-${index}`}
      aria-labelledby={`machine-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// 🎨 Компонент статистики машини
const MachineStats = ({ cycles, machine }) => {
  const theme = useTheme();

  const stats = useMemo(() => {
    const totalCycles = cycles.length;
    const totalDuration = cycles.reduce((sum, cycle) => sum + (cycle.duration || 0), 0);
    const avgDuration = totalCycles > 0 ? totalDuration / totalCycles : 0;
    
    const programUsage = cycles.reduce((acc, cycle) => {
      const program = cycle.program || 'Unknown';
      acc[program] = (acc[program] || 0) + 1;
      return acc;
    }, {});

    const topPrograms = Object.entries(programUsage)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([name, count]) => ({ name, count }));

    return {
      totalCycles,
      totalDuration,
      avgDuration,
      topPrograms
    };
  }, [cycles]);

  const pieData = [
    { name: 'Activo', value: machine.status === 'Disponible' || machine.status === 'En Uso' ? 75 : 0 },
    { name: 'Inactivo', value: machine.status === 'Requiere Servicio' ? 100 : 25 }
  ];

  const COLORS = [theme.palette.success.main, theme.palette.grey[400]];

  const StatCard = ({ title, value, subtitle, color = "primary", icon: Icon }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette[color].dark }}>
            {title}
          </Typography>
          <Avatar sx={{ bgcolor: alpha(theme.palette[color].main, 0.1), width: 40, height: 40 }}>
            <Icon sx={{ color: theme.palette[color].main }} />
          </Avatar>
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette[color].dark }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Total Ciclos"
          value={stats.totalCycles}
          subtitle="este mes"
          color="primary"
          icon={Analytics}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Tiempo Total"
          value={`${Math.round(stats.totalDuration / 60)}h`}
          subtitle="de operación"
          color="info"
          icon={History}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Duración Promedio"
          value={`${stats.avgDuration.toFixed(0)}m`}
          subtitle="por ciclo"
          color="success"
          icon={Speed}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Estado Actual
            </Typography>
            <Box sx={{ textAlign: 'center' }}>
              <ResponsiveContainer width="100%" height={120}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={50}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <Chip 
                label={machine.status} 
                color={
                  machine.status === 'Disponible' ? 'success' :
                  machine.status === 'En Uso' ? 'info' : 'error'
                }
                sx={{ mt: 1 }}
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default function MachineDetailsPage({ user }) {
  const { machineId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);

  const [machine, setMachine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);

  // Стани форми циклу
  const [client, setClient] = useState('');
  const [program, setProgram] = useState('');
  const [duration, setDuration] = useState('');

  // Нотатки та журнал обслуговування
  const [technicianNote, setTechnicianNote] = useState('');
  const [maintenanceLog, setMaintenanceLog] = useState([]);
  const [newLogEntry, setNewLogEntry] = useState('');

  // Цикли прання
  const [cycles, setCycles] = useState([]);

  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      try {
        // 1. Завантаження статусу машини
        const allMachines = await getData('machine_statuses') || initialMachineData;
        const foundMachine = allMachines.find(m => m.id == machineId);
        setMachine(foundMachine);

        // 2. Завантаження нотаток техніка
        const allNotes = await getData('machine_notes') || {};
        setTechnicianNote(allNotes[machineId] || '');

        // 3. Завантаження журналу обслуговування
        const allLogs = await getData('machine_maintenance_logs') || {};
        setMaintenanceLog(allLogs[machineId] || []);

        // 4. Завантаження циклів цієї машини
        const savedCycles = await getData(`machine_${machineId}_cycles`) || [];
        setCycles(savedCycles);
      } catch (error) {
        console.error("Error loading machine details:", error);
      } finally {
        setLoading(false);
      }
    };
    loadAllData();
  }, [machineId]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Збереження замітки
  const handleSaveNote = async () => {
    const allNotes = await getData('machine_notes') || {};
    allNotes[machineId] = technicianNote;
    await saveData('machine_notes', allNotes);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  // Додавання запису в журнал обслуговування
  const handleAddLogEntry = async () => {
    if (newLogEntry.trim() === '') return;
    const newEntry = { 
      id: Date.now(), 
      date: new Date().toISOString(), 
      technician: user?.username || 'Técnico', 
      note: newLogEntry.trim() 
    };
    
    const allLogs = await getData('machine_maintenance_logs') || {};
    const currentMachineLogs = allLogs[machineId] || [];
    const updatedLogs = [newEntry, ...currentMachineLogs];
    allLogs[machineId] = updatedLogs;

    await saveData('machine_maintenance_logs', allLogs);
    setMaintenanceLog(updatedLogs);
    setNewLogEntry('');
  };

  // Додавання нового циклу прання
  const handleAddCycle = async () => {
    if (!client || !program || !duration) {
      alert('Por favor, complete todos los campos para iniciar el ciclo.');
      return;
    }
    const newCycle = {
      id: Date.now(),
      client: CLIENTS.find(c => c.code === client)?.name || '',
      program: PROGRAMS.find(p => p.code === program)?.name || '',
      duration: Number(duration),
      shift: machine?.status === 'En Uso' ? 'En Uso' : 'Disponible',
      timestamp: new Date().toISOString()
    };
    const updatedCycles = [...cycles, newCycle];
    setCycles(updatedCycles);
    await saveData(`machine_${machineId}_cycles`, updatedCycles);

    setClient('');
    setProgram('');
    setDuration('');
  };

  // Підрахунок тривалості циклів
  const chartData = useMemo(() => {
    const morningSum = cycles.filter(c => c.shift === 'mañana' || c.shift === 'Disponible').reduce((acc, cur) => acc + cur.duration, 0);
    const eveningSum = cycles.filter(c => c.shift === 'tarde' || c.shift === 'En Uso').reduce((acc, cur) => acc + cur.duration, 0);
    return [
      { name: 'Turno Mañana/Disponible', duration: morningSum },
      { name: 'Turno Tarde/En Uso', duration: eveningSum },
    ];
  }, [cycles]);

  if (loading || !machine) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          Cargando detalles de la máquina...
        </Typography>
      </Box>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Disponible': return 'success';
      case 'En Uso': return 'info';
      case 'Requiere Servicio': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Заголовок та навігація */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button 
              variant="outlined" 
              startIcon={<ArrowBack />} 
              onClick={() => navigate('/machines')}
              sx={{ borderRadius: 2 }}
            >
              Volver al listado
            </Button>
            <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 56, height: 56 }}>
              <LocalLaundryService sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 800 }}>
                {machine.name}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                {machine.model}
              </Typography>
            </Box>
          </Box>
          <Chip 
            label={machine.status}
            color={getStatusColor(machine.status)}
            size="large"
            icon={
              machine.status === 'Disponible' ? <CheckCircle /> :
              machine.status === 'En Uso' ? <PlayCircleOutline /> :
              <Warning />
            }
            sx={{ fontWeight: 700, fontSize: '1rem', px: 2 }}
          />
        </Box>

        {/* Основна інформація */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {machine.imageUrl && (
              <CardMedia 
                component="img" 
                height="300" 
                image={machine.imageUrl} 
                alt={machine.name} 
                sx={{ 
                  objectFit: 'contain', 
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.background.default, 0.5),
                  filter: machine.status !== 'Disponible' && machine.status !== 'En Uso' ? 'grayscale(80%) opacity(0.7)' : 'none'
                }} 
              />
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Especificaciones Técnicas
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Scale sx={{ color: 'text.secondary' }} />
                  <Typography>Capacidad: <strong>{machine.capacity} kg</strong></Typography>
                </Box>
                {machine.maxLoad && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Speed sx={{ color: 'text.secondary' }} />
                    <Typography>Carga máxima recomendada: <strong>{machine.maxLoad} kg</strong></Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Build sx={{ color: 'text.secondary' }} />
                  <Typography>Modelo: <strong>{machine.model}</strong></Typography>
                </Box>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Вкладки */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTab-root': { fontWeight: 600 }
          }}
        >
          <Tab icon={<Analytics />} label="Estadísticas" />
          <Tab icon={<PlayCircleOutline />} label="Nuevo Ciclo" />
          <Tab icon={<Note />} label="Notas Técnicas" />
          <Tab icon={<History />} label="Historial" />
        </Tabs>

        {/* Вкладка статистики */}
        <TabPanel value={tabValue} index={0}>
          <MachineStats cycles={cycles} machine={machine} />
          
          {/* Графіки */}
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Duración por Turno
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="duration" fill={theme.palette.primary.main} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Ciclos Registrados
                  </Typography>
                  {cycles.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">
                        No hay ciclos registrados para esta máquina.
                      </Typography>
                    </Box>
                  ) : (
                    <TableContainer sx={{ maxHeight: 300 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Cliente</TableCell>
                            <TableCell>Programa</TableCell>
                            <TableCell align="right">Duración (min)</TableCell>
                            <TableCell>Fecha</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cycles.slice(0, 10).map(cycle => (
                            <TableRow key={cycle.id}>
                              <TableCell>{cycle.client}</TableCell>
                              <TableCell>{cycle.program}</TableCell>
                              <TableCell align="right">{cycle.duration}</TableCell>
                              <TableCell>
                                {new Date(cycle.timestamp).toLocaleDateString()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Вкладка нового циклу */}
        <TabPanel value={tabValue} index={1}>
          <Card>
            <CardContent>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PlayCircleOutline />
                Registrar Nuevo Ciclo
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Cliente</InputLabel>
                    <Select 
                      value={client} 
                      label="Cliente" 
                      onChange={e => setClient(e.target.value)}
                    >
                      {CLIENTS.map(c => <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Programa</InputLabel>
                    <Select 
                      value={program} 
                      label="Programa" 
                      onChange={e => setProgram(e.target.value)}
                    >
                      {PROGRAMS.map(p => <MenuItem key={p.code} value={p.code}>{p.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField 
                    label="Duración del ciclo (minutos)" 
                    type="number" 
                    fullWidth 
                    value={duration} 
                    onChange={e => setDuration(e.target.value)} 
                  />
                </Grid>
              </Grid>
              <Button 
                variant="contained" 
                size="large" 
                startIcon={<PlayCircleOutline />} 
                sx={{ mt: 3, px: 4 }}
                onClick={handleAddCycle}
              >
                Iniciar Ciclo
              </Button>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Вкладка нотаток */}
        <TabPanel value={tabValue} index={2}>
          <Card>
            <CardContent>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Note />
                Observaciones del Técnico
              </Typography>
              <TextField 
                label="Mensaje para los operadores" 
                multiline 
                rows={6} 
                fullWidth 
                value={technicianNote} 
                onChange={e => setTechnicianNote(e.target.value)} 
                InputProps={{ readOnly: user?.role !== 'technician' }} 
                variant={user?.role === 'technician' ? 'outlined' : 'filled'} 
              />
              {user?.role === 'technician' && (
                <Box sx={{ mt: 2, textAlign: 'right' }}>
                  <Button 
                    variant="contained" 
                    startIcon={<Save />} 
                    onClick={handleSaveNote}
                    sx={{ px: 4 }}
                  >
                    Guardar Nota
                  </Button>
                </Box>
              )}
              {saved && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Observación guardada correctamente.
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        {/* Вкладка історії */}
        <TabPanel value={tabValue} index={3}>
          <Card>
            <CardContent>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <History />
                Historial de Mantenimiento
              </Typography>
              
              {user?.role === 'technician' && (
                <Box sx={{ mb: 3 }}>
                  <TextField 
                    label="Nuevo registro de mantenimiento" 
                    multiline 
                    rows={3} 
                    fullWidth 
                    variant="outlined" 
                    value={newLogEntry} 
                    onChange={e => setNewLogEntry(e.target.value)} 
                  />
                  <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<Add />} 
                    sx={{ mt: 1 }}
                    onClick={handleAddLogEntry}
                  >
                    Añadir al historial
                  </Button>
                </Box>
              )}

              <List sx={{ maxHeight: 400, overflowY: 'auto' }}>
                {maintenanceLog.length > 0 ? (
                  maintenanceLog.map((entry, index) => (
                    <React.Fragment key={entry.id}>
                      <ListItem alignItems="flex-start">
                        <ListItemText 
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
                                <Build sx={{ fontSize: 18 }} />
                              </Avatar>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {entry.note}
                              </Typography>
                            </Box>
                          } 
                          secondary={`— ${entry.technician} | ${new Date(entry.date).toLocaleString('es-ES')}`} 
                        />
                      </ListItem>
                      {index < maintenanceLog.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <History sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      No hay registros de mantenimiento para esta máquina.
                    </Typography>
                  </Box>
                )}
              </List>
            </CardContent>
          </Card>
        </TabPanel>
      </Paper>
    </Box>
  );
}


